/**
 * What a pay increase does to each contract's margin, and the charge-rate rise
 * that would undo it.
 *
 * Two reports over the same shifts are compared: the roster at today's rates,
 * and the same roster with the scenario applied. Charge rates are the same in
 * both, so the whole difference is labour cost.
 */

import { roundMoney } from '../payroll/time';
import type {
  ContractMargin,
  ContractScenario,
  MarginLine,
  MarginReport,
  ProfitabilitySettings,
  ScenarioLine,
  ScenarioOutcome,
  ScenarioReport,
  SiteMargin,
  SiteScenario,
} from './types';

/**
 * The rise in every charge rate, as a percentage, that would leave this labour
 * cost earning `marginPercent`. Sunday and public-holiday charges are multiples
 * of the hourly rate, so revenue moves by the same percentage as the rate.
 */
export function requiredUpliftPercent(
  revenue: number | null,
  labourCost: number,
  marginPercent: number | null,
): number | null {
  if (revenue === null || revenue <= 0 || marginPercent === null || marginPercent >= 100) return null;
  const requiredRevenue = labourCost / (1 - (marginPercent / 100));
  return ((requiredRevenue / revenue) - 1) * 100;
}

export function scenarioOutcome(before: MarginLine, after: MarginLine): ScenarioOutcome {
  if (before.status === 'no_charge_rate' || after.status === 'no_charge_rate') return 'unpriced';
  if (before.status === 'loss') return 'already_loss_making';
  if (after.status === 'loss') return 'turns_loss_making';
  if (after.status === 'on_target') return 'stays_on_target';
  return before.status === 'on_target' ? 'falls_below_target' : 'stays_below_target';
}

function compareLines(before: MarginLine, after: MarginLine, settings: ProfitabilitySettings): ScenarioLine {
  const upliftToReachTarget = requiredUpliftPercent(after.revenue, after.labourCost, settings.targetMarginPercent);

  return {
    before,
    after,
    labourCostChange: roundMoney(after.labourCost - before.labourCost),
    marginPointChange: before.marginPercent !== null && after.marginPercent !== null
      ? after.marginPercent - before.marginPercent
      : null,
    outcome: scenarioOutcome(before, after),
    upliftToHoldMargin: requiredUpliftPercent(after.revenue, after.labourCost, before.marginPercent),
    upliftToReachTarget: upliftToReachTarget === null ? null : Math.max(0, upliftToReachTarget),
  };
}

const OUTCOME_ORDER: ScenarioOutcome[] = [
  'turns_loss_making',
  'already_loss_making',
  'falls_below_target',
  'stays_below_target',
  'stays_on_target',
  'unpriced',
];

/** Margin after the change, lowest first, within each outcome. */
function hardestHitFirst<T extends ScenarioLine>(lines: T[]): T[] {
  return lines.sort((left, right) => (
    OUTCOME_ORDER.indexOf(left.outcome) - OUTCOME_ORDER.indexOf(right.outcome)
    || (left.after.marginPercent ?? Infinity) - (right.after.marginPercent ?? Infinity)
  ));
}

function matchSites(before: SiteMargin[], after: SiteMargin[], settings: ProfitabilitySettings): SiteScenario[] {
  const afterById = new Map(after.map((site) => [site.siteId, site]));
  const sites: SiteScenario[] = [];

  before.forEach((site) => {
    const match = afterById.get(site.siteId);
    if (!match) return;
    sites.push({
      ...compareLines(site, match, settings),
      siteId: site.siteId,
      siteName: site.siteName,
      chargeRate: site.chargeRate,
    });
  });

  return hardestHitFirst(sites);
}

/**
 * Compare two reports built from the same shifts. A contract in one and not
 * the other cannot be compared and is left out.
 */
export function compareScenario(
  before: MarginReport,
  after: MarginReport,
  settings: ProfitabilitySettings,
): ScenarioReport {
  const afterById = new Map<string | null, ContractMargin>(
    after.contracts.map((contract) => [contract.contractId, contract]),
  );
  const contracts: ContractScenario[] = [];

  before.contracts.forEach((contract) => {
    const match = afterById.get(contract.contractId);
    if (!match) return;
    contracts.push({
      ...compareLines(contract, match, settings),
      contractId: contract.contractId,
      contractName: contract.contractName,
      sites: matchSites(contract.sites, match.sites, settings),
    });
  });

  return {
    contracts: hardestHitFirst(contracts),
    priced: compareLines(before.priced, after.priced, settings),
    turnsLossMakingCount: contracts.filter((contract) => contract.outcome === 'turns_loss_making').length,
    alreadyLossMakingCount: contracts.filter((contract) => contract.outcome === 'already_loss_making').length,
    fallsBelowTargetCount: contracts.filter((contract) => contract.outcome === 'falls_below_target').length,
  };
}
